<script setup lang="ts">
import DateSpecificHours from '@/assets/date-specific-hours.png'
import DeleteConfirmation from '@/components/common/DeleteConfirmation.vue'
import AppState from '@/states/app'
import { DatePickerLanguageMapper, GHLLanguageKeysMapper } from '@/utils/constants'
import { computedTimezones } from '@/utils/timezonesOptions'
import { UIMultiDatePicker } from '@gohighlevel/ghl-calendar-element'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Globe05Icon,
  Pencil01Icon,
  PlusIcon,
  Trash01Icon,
  XCloseIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIAvatar,
  UIButton,
  UIContentWrap,
  UIFormItem,
  UIModal,
  UIModalContent,
  UIModalFooter,
  UISelect,
  UISkeleton,
  UITimePicker,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import moment from 'moment'
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getInitials } from '../../utils/generalFunctions'
import { Interval } from './DayAvailability'
import {
  MultiUserSchedule
} from './MultiUserSchedule'

moment.locale(GHLLanguageKeysMapper[AppState.locale])

const { t } = useI18n()

const props = defineProps<{
  multiUserSchedule: MultiUserSchedule
  isRecurringCalendarSelected: boolean
  isSaving: boolean
}>()

const emit = defineEmits<{
  (e: 'update'): void
  (e: 'save', timezone: string): void
}>()

// --- Refs ---
const showOverRideModal = ref(false)
const editMode = ref(false)
const editingUserId = ref<string | null>(null) // null means editing for all users
const originalEditDate = ref<string | null>(null)
const expandedDates = ref<Set<string>>(new Set())
// Multi-date selection - array of ISO date strings
const overriddenDates = ref<string[]>([])
const overriddenHours = ref<{ startTime: string; endTime: string }[]>([
  { startTime: '8:0', endTime: '17:0' },
])
const customSlotSelectorModal = ref<HTMLElement | null>(null)
// Selected timezone for the modal
const selectedTimezone = ref<string>(props.multiUserSchedule?.timezone || AppState.locationTimezone)
// Loading state to prevent flash of empty state during save
// Cache the previous entries count to prevent flash of empty state
const previousEntriesCount = ref(0)
// Date selection error
const showDateSelectionError = ref(false)

// Locale for date picker
const locale = computed(() => {
  return DatePickerLanguageMapper[AppState.locale]
})

// Delete confirmation modal state
const showDeleteConfirmModal = ref(false)
const deleteTargetUserId = ref<string | null>(null) // null means delete for all users on the date
const deleteTargetDate = ref<string>('')
const deleteTargetUserName = ref<string>('')

// User list expansion state for "Assign to" section
const isUserListExpanded = ref(false)
const MAX_VISIBLE_USERS = 6

// --- Computed ---
const groupedDateEntries = computed(() => {
  return props.multiUserSchedule?.getGroupedDateSpecificHours() || []
})

const selectedUsers = computed(() => {
  return props.multiUserSchedule?.getUsers() || []
})

// Visible users (first 6) and remaining count for truncated display
const visibleUsers = computed(() => {
  if (isUserListExpanded.value) {
    return selectedUsers.value
  }
  return selectedUsers.value.slice(0, MAX_VISIBLE_USERS)
})

const remainingUsersCount = computed(() => {
  return Math.max(0, selectedUsers.value.length - MAX_VISIBLE_USERS)
})

const hasMoreUsers = computed(() => {
  return selectedUsers.value.length > MAX_VISIBLE_USERS
})

const invalidTimes = computed(() => {
  return (
    overriddenHours.value.filter(x => {
      if (
        (x.startTime === '0:0' && x.endTime === '0:0') ||
        x.endTime === '0:0'
      ) {
        return false
      }
      return (
        moment(x.startTime, 'H:m').isSameOrAfter(moment(x.endTime, 'H:m')) ||
        moment(x.endTime, 'H:m').isSameOrBefore(moment(x.startTime, 'H:m'))
      )
    }).length > 0
  )
})

const is24HourSelected = computed(() => {
  return overriddenHours.value.length === 1
    ? overriddenHours.value[0].startTime === '0:0' &&
        overriddenHours.value[0].endTime === '0:0'
    : false
})

const overlappingTimes = computed(() => {
  const overlaps: number[] = []

  if (
    overriddenHours.value.length === 1 &&
    overriddenHours.value[0].startTime === '0:0' &&
    overriddenHours.value[0].endTime === '0:0'
  ) {
    return []
  }

  // Skip overlap check if no dates selected
  if (overriddenDates.value.length === 0) {
    return []
  }

  for (let i = 0; i < overriddenHours.value.length - 1; i++) {
    for (let j = i + 1; j < overriddenHours.value.length; j++) {
      const startTime1 = moment(overriddenHours.value[i].startTime, 'H:mm')
      const endTime1 = moment(overriddenHours.value[i].endTime, 'H:mm')
      const startTime2 = moment(overriddenHours.value[j].startTime, 'H:mm')
      const endTime2 = moment(overriddenHours.value[j].endTime, 'H:mm')

      if (
        startTime1.isBetween(startTime2, endTime2) ||
        endTime1.isBetween(startTime2, endTime2) ||
        startTime2.isBetween(startTime1, endTime1) ||
        endTime2.isBetween(startTime1, endTime1)
      ) {
        overlaps.push(i)
        overlaps.push(j)
      }
    }
  }

  return [...new Set(overlaps)]
})

// --- Methods ---
const toggleDateExpanded = (date: string) => {
  if (expandedDates.value.has(date)) {
    expandedDates.value.delete(date)
  } else {
    expandedDates.value.add(date)
  }
  expandedDates.value = new Set(expandedDates.value)
}

const isDateExpanded = (date: string) => {
  return expandedDates.value.has(date)
}

// Watch for date selection to clear error
watch(() => overriddenDates.value, (newVal) => {
  if (newVal.length > 0) {
    showDateSelectionError.value = false
  }
})

const openOverrideModal = () => {
  showOverRideModal.value = true
  editMode.value = false
  editingUserId.value = null
  originalEditDate.value = null
  overriddenHours.value = [{ startTime: '8:0', endTime: '17:0' }]
  overriddenDates.value = []
  showDateSelectionError.value = false
  // Reset timezone to the multiUserSchedule timezone
  selectedTimezone.value = props.multiUserSchedule?.timezone || AppState.locationTimezone
  // Reset user list expansion state
  isUserListExpanded.value = false
}

const handleEditForAllUsers = (date: string, intervals: Interval[]) => {
  editMode.value = true
  editingUserId.value = null
  originalEditDate.value = moment(date).format('YYYY-MM-DD')
  // Convert to ISO string for multi-date picker
  overriddenDates.value = [moment(date).startOf('day').toISOString()]
  overriddenHours.value = intervals.map(i => ({
    startTime: i.from,
    endTime: i.to,
  }))
  showDateSelectionError.value = false
  showOverRideModal.value = true
}

const handleEditForUser = (
  userId: string,
  date: string,
  intervals: Interval[]
) => {
  editMode.value = true
  editingUserId.value = userId
  originalEditDate.value = moment(date).format('YYYY-MM-DD')
  // Convert to ISO string for multi-date picker
  overriddenDates.value = [moment(date).startOf('day').toISOString()]
  overriddenHours.value = intervals.map(i => ({
    startTime: i.from,
    endTime: i.to,
  }))
  showDateSelectionError.value = false
  showOverRideModal.value = true
}

// Show delete confirmation modal for a specific user's date hours
const showDeleteConfirmForUser = (userId: string, userName: string, date: string) => {
  deleteTargetUserId.value = userId
  deleteTargetDate.value = date
  deleteTargetUserName.value = userName
  showDeleteConfirmModal.value = true
}

// Show delete confirmation modal for all users on a date
const showDeleteConfirmForDate = (date: string) => {
  deleteTargetUserId.value = null // null means delete for all users
  deleteTargetDate.value = date
  deleteTargetUserName.value = ''
  showDeleteConfirmModal.value = true
}

// Cancel delete
const cancelDelete = () => {
  showDeleteConfirmModal.value = false
  deleteTargetUserId.value = null
  deleteTargetDate.value = ''
  deleteTargetUserName.value = ''
}

// Confirm and execute delete
const confirmDelete = () => {
  // Cache previous count to prevent flash of empty state
  previousEntriesCount.value = groupedDateEntries.value.length
  
  if (deleteTargetUserId.value) {
    // Delete for specific user
    props.multiUserSchedule.deleteDateSpecificHoursForUser(
      deleteTargetUserId.value,
      deleteTargetDate.value
    )
  } else {
    // Delete for all users on this date
    props.multiUserSchedule.deleteDateSpecificHoursForAllUsers(deleteTargetDate.value)
  }
  
  // Close the confirmation modal
  showDeleteConfirmModal.value = false
  deleteTargetUserId.value = null
  deleteTargetDate.value = ''
  deleteTargetUserName.value = ''
  
  // Emit save to trigger immediate save in bulk edit mode (use existing timezone for deletes)
  emit('save', props.multiUserSchedule?.timezone || AppState.locationTimezone)
}

const onClickAddSpecificHours = () => {
  if (overlappingTimes.value.length > 0 || !props.multiUserSchedule) {
    return
  }

  // Validate date selection
  if (overriddenDates.value.length === 0) {
    showDateSelectionError.value = true
    return
  }

  const intervals: Interval[] = overriddenHours.value.map(x => ({
    from: x.startTime,
    to: x.endTime,
  }))

  const newDates = overriddenDates.value.map(dateString => 
    moment(new Date(dateString)).format('YYYY-MM-DD')
  )

  if (editMode.value && originalEditDate.value && !newDates.includes(originalEditDate.value)) {
    if (editingUserId.value) {
      props.multiUserSchedule.deleteDateSpecificHoursForUser(editingUserId.value, originalEditDate.value)
    } else {
      props.multiUserSchedule.deleteDateSpecificHoursForAllUsers(originalEditDate.value)
    }
  }

  newDates.forEach(date => {
    if (editingUserId.value) {
      // Edit for specific user only
      props.multiUserSchedule.updateDateSpecificHoursForUser(
        editingUserId.value,
        date,
        intervals
      )
    } else {
      // Add for all selected users (consistent with bulk edit flow)
      props.multiUserSchedule.addDateSpecificHoursForAllUsers(date, intervals)
    }
  })

  // Emit save to trigger immediate save in bulk edit mode (include selected timezone)
  emit('save', selectedTimezone.value)
  cancelCallback()
}

const cancelCallback = () => {
  showOverRideModal.value = false
  overriddenDates.value = []
  overriddenHours.value = [{ startTime: '8:0', endTime: '17:0' }]
  editMode.value = false
  editingUserId.value = null
  originalEditDate.value = null
  showDateSelectionError.value = false
}


const addNextSlots = async (item: { startTime: string; endTime: string }) => {
  overriddenHours.value.push({
    startTime: moment(item.endTime, 'H:m')
      .set({ hour: moment(item.endTime, 'H:m').hour() + 1 })
      .format('H:m'),
    endTime: moment(item.endTime, 'H:m')
      .set({ hour: moment(item.endTime, 'H:m').hour() + 2 })
      .format('H:m'),
  })
  await nextTick()
  if (customSlotSelectorModal.value) {
    customSlotSelectorModal.value.scrollTop =
      customSlotSelectorModal.value.scrollHeight
  }
}

const removeHours = (index: number) => {
  if (overriddenHours.value.length > 0) {
    overriddenHours.value.splice(index, 1)
  }
}

// Date validation for multi-date picker
const isDateDisabled = (date: Date) => {
  const selectedDate = moment(date).startOf('day')
  const currentDate = moment().tz(AppState.locationTimezone).startOf('day')
  return selectedDate.isBefore(currentDate)
}

// Minimum date for date picker
const minDate = computed(() => {
  return moment().tz(AppState.locationTimezone).startOf('day').toDate()
})

const formatTime = (time: string) => {
  return moment(time, 'H:m').format('hh:mm A')
}

const formatDate = (date: string) => {
  return moment(date).format('D MMM, YYYY')
}

const getEditingUserName = computed(() => {
  if (!editingUserId.value) return ''
  const user = props.multiUserSchedule.getUser(editingUserId.value)
  return user?.name || ''
})

// Watch for changes in the schedule to auto-expand new dates and reset saving state
watch(
  groupedDateEntries,
  newEntries => {
    newEntries.forEach(entry => {
      if (!expandedDates.value.has(entry.date)) {
        expandedDates.value.add(entry.date)
      }
    })
  },
  { immediate: true }
)
</script>

<template>
  <UIContentWrap :locale="GHLLanguageKeysMapper[AppState.locale]" class="!p-0">
    <div class="mb-4 flex flex-col gap-1 text-sm">
      <h4 class="text-sm font-semibold">
        {{ $t('calendar_advanced.availability.specific_hours_button') }}
      </h4>
      <p class="text-sm text-gray-500">
        {{ $t('calendar_advanced.availability.specific_hours_description') }}
      </p>
    </div>

    <!-- Add Button -->
    <div class="flex">
      <div v-if="props.isRecurringCalendarSelected">
        <UITooltip placement="top">
          <template #trigger>
            <UIButton
              type="default"
              :disabled="props.isRecurringCalendarSelected"
              @click="openOverrideModal"
            >
              <PlusIcon class="mr-2 h-4 w-4" />
              {{ $t('calendar_advanced.availability.date_specific_level') }}
            </UIButton>
          </template>
          <p class="max-w-sm">
            {{
              $t(
                'settings.availability.schedule.date_specific_hours_recurring_meetings_enabled'
              )
            }}
          </p>
        </UITooltip>
      </div>
      <UIButton
        v-else
        type="default"
        :disabled="props.isRecurringCalendarSelected"
        @click="openOverrideModal"
      >
        <PlusIcon class="mr-2 h-4 w-4" />
        {{ $t('calendar_advanced.availability.date_specific_level') }}
      </UIButton>
    </div>

    <!-- Loading State during save (Skeleton UI) -->
    <div
      v-if="props.isSaving"
      class="mt-4 flex flex-col gap-3"
    >
      <div v-for="i in 3" :key="i" class="border-b border-gray-200 pb-3">
        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-3">
            <UISkeleton height="16px" width="16px" :sharp="false" class="!rounded" />
            <UISkeleton height="20px" width="120px" :sharp="false" class="!rounded" />
          </div>
          <UISkeleton height="24px" width="24px" :sharp="false" class="!rounded" />
        </div>
        <div class="px-4 py-2">
          <div class="flex items-center gap-3 mb-2">
            <UISkeleton height="32px" width="32px" :sharp="false" class="!rounded-full" />
            <UISkeleton height="16px" width="100px" :sharp="false" class="!rounded" />
            <UISkeleton height="16px" width="140px" :sharp="false" class="!rounded" />
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State (only show when not saving) -->
    <div
      v-else-if="!props.isSaving && groupedDateEntries.length === 0"
      class="my-20 flex flex-col items-center justify-center"
    >
      <img
        :src="DateSpecificHours"
        class="mb-2 h-36"
        :alt="$t('calendar_advanced.availability.specific_hours_button')"
      />
      <p class="text-md text-center font-semibold text-gray-900">
        {{ $t('calendar_advanced.availability.no_date_specific_heading') }}
      </p>
      <p class="text-center text-xs text-gray-600">
        {{ $t('calendar_advanced.availability.no_date_specific_description') }}
      </p>
    </div>

    <!-- Collapsible Date-wise List -->
    <div v-else class="mt-4 flex flex-col gap-2">
      <div
        v-for="(entry, index) in groupedDateEntries"
        :key="entry.date"
        class="bg-white overflow-hidden"
        :class="{
          'border-b border-gray-200': index !== groupedDateEntries.length - 1,
        }"
      >
        <!-- Date Header (Collapsible) -->
        <div
          class="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
          role="button"
          tabindex="0"
          @click="toggleDateExpanded(entry.date)"
          @keydown.enter="toggleDateExpanded(entry.date)"
        >
          <div class="flex items-center gap-3">
            <component
              :is="isDateExpanded(entry.date) ? ChevronUpIcon : ChevronDownIcon"
              class="w-4 h-4 text-gray-500"
            />
            <span class="text-sm font-medium text-gray-700">
              {{ formatDate(entry.date) }}
            </span>
            <span class="text-xs text-gray-500">
              ({{ entry.users.length === 1 
                ? t('settings.availability.schedule.bulk_edit_user_count', { count: entry.users.length })
                : t('settings.availability.schedule.bulk_edit_users_count', { count: entry.users.length }) 
              }})
            </span>
          </div>
          <UIButton
            class="text-gray-500 hover:text-red-600"
            :text="true"
            @click.stop="showDeleteConfirmForDate(entry.date)"
          >
            <Trash01Icon class="w-4 h-4" />
          </UIButton>
        </div>

        <!-- User-wise Slots (Expanded Content) -->
        <div
          v-if="isDateExpanded(entry.date)"
          class=""
        >
          <div
            v-for="(userEntry, idx) in entry.users"
            :key="userEntry.userId"
            class="flex items-start justify-between px-4 py-3"
          >
            <div class="flex items-start gap-3">
              <!-- User Avatar -->
              <div
                class="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-200"
              >
                <UIAvatar
                  v-if="userEntry.userPhoto"
                  :src="userEntry.userPhoto"
                  :alt="userEntry.userName"
                  class="h-8 w-8 text-xs"
                />
                <span v-else class="text-xs font-medium text-gray-600">
                  {{ getInitials(userEntry.userName) }}
                </span>
              </div>

              <!-- User Name & Intervals -->
              <div class="flex flex-col min-w-0 flex-1">
                <span 
                  class="text-sm font-medium text-gray-800 truncate max-w-[200px]"
                  :title="userEntry.userName"
                >
                  {{ userEntry.userName }}
                </span>
                <div class="flex flex-col gap-1 mt-1">
                  <span
                    v-if="userEntry.intervals.length === 0"
                    class="text-xs text-gray-500"
                  >
                    {{ $t('calendar_advanced.availability.unavailable_label') }}
                  </span>
                  <span
                    v-for="(interval, i) in userEntry.intervals"
                    v-else
                    :key="i"
                    class="text-xs text-gray-500"
                  >
                    {{ formatTime(interval.from) }} -
                    {{ formatTime(interval.to) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-4">
              <UIButton
                class="text-gray-500 hover:text-blue-600"
                :text="true"
                @click="
                  handleEditForUser(
                    userEntry.userId,
                    entry.date,
                    userEntry.intervals
                  )
                "
              >
                <Pencil01Icon class="w-4 h-4" />
              </UIButton>
              <UIButton
                class="text-gray-500 hover:text-red-600"
                :text="true"
                @click="showDeleteConfirmForUser(userEntry.userId, userEntry.userName, entry.date)"
              >
                <Trash01Icon class="w-4 h-4" />
              </UIButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <UIModal
      id="date-specific-hours-modal"
      v-model:show="showOverRideModal"
      :mask-closable="false"
      preset="card"
      class="max-h-[90vh]"
      width="450"
    >
      <template #header>
        <div class="flex w-full items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900">
            {{ editingUserId ? $t('settings.availability.schedule.set_date_specific_hours_for_user') : $t('settings.availability.schedule.set_date_specific_hours_for_users', { count: selectedUsers.length })}}
          </h3>
          <XCloseIcon
            class="h-5 w-5 cursor-pointer text-gray-500 hover:text-gray-700"
            role="button"
            tabindex="0"
            :aria-label="$t('aria_labels.close_modal')"
            @click="cancelCallback"
            @keydown.enter="cancelCallback"
            @keydown.space.prevent="cancelCallback"
          />
        </div>
      </template>

      <UIModalContent class="max-h-[calc(90vh-140px)] overflow-y-auto">
        <!-- User Selection for New Date-Specific Hours -->
        <div class="mb-4">
          <!-- Editing specific user - show non-editable display -->
          <div v-if="editingUserId" class="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <span class="text-sm font-medium text-gray-700 mb-2 block">
              {{ t('settings.availability.schedule.bulk_edit_editing_for') }}
            </span>
            <div class="flex flex-wrap gap-2">
              <div
                class="inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 pl-1 pr-3 py-1 max-w-[180px]"
                :title="getEditingUserName"
              >
                <div
                  class="flex h-6 w-6 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
                >
                  <span class="text-[10px] font-medium text-gray-600">
                    {{ getInitials(getEditingUserName) }}
                  </span>
                </div>
                <span class="text-sm text-gray-700 truncate">{{ getEditingUserName }}</span>
              </div>
            </div>
          </div>
          <!-- Adding new - show non-editable user chips (consistent with BulkEditWeeklyHoursModal) -->
          <div v-else class="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <span class="text-sm font-medium text-gray-700 mb-2 block">
              {{ $t('settings.availability.schedule.selected_users') }}
            </span>
            <div class="flex flex-wrap gap-2">
              <!-- Visible user chips (up to 6, or all if expanded) -->
              <div
                v-for="user in visibleUsers"
                :key="user.id"
                class="inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 pl-1 pr-3 py-1 max-w-[180px]"
                :title="user.name"
              >
                <div
                  class="flex h-6 w-6 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
                >
                  <UIAvatar
                    v-if="user.profilePhoto"
                    :src="user.profilePhoto"
                    :alt="user.name"
                    class="h-6 w-6 text-[10px]"
                  />
                  <span v-else class="text-[10px] font-medium text-gray-600">
                    {{ getInitials(user.name) }}
                  </span>
                </div>
                <span class="text-sm text-gray-700 truncate">{{ user.name }}</span>
              </div>
              <!-- "+X more" chip with chevron when there are more than 6 users -->
              <div
                v-if="hasMoreUsers"
                class="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-3 py-1 cursor-pointer hover:bg-gray-50 transition-colors"
                role="button"
                tabindex="0"
                @click="isUserListExpanded = !isUserListExpanded"
                @keydown.enter="isUserListExpanded = !isUserListExpanded"
              >
                <span class="text-sm text-gray-700">
                  {{ isUserListExpanded ? t('settings.availability.schedule.show_less') : `+${remainingUsersCount} ${t('settings.availability.schedule.more')}` }}
                </span>
                <component
                  :is="isUserListExpanded ? ChevronUpIcon : ChevronDownIcon"
                  class="h-4 w-4 text-gray-500"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Timezone Selector -->
        <UIFormItem
          class="w-full mb-4"
          :label="$t('settings.availability.schedule.timezone')"
          :icon="Globe05Icon"
          :show-feedback="false"
        >
          <UISelect
            id="date-specific-timezone-select"
            v-model:value="selectedTimezone"
            class="w-full px-1"
            :options="computedTimezones"
            :filterable="true"
            :placeholder="$t('settings.availability.schedule.timezone_placeholder')"
          />
        </UIFormItem>

        <!-- Date Picker -->
        <div
          id="do-picker-multi"
          class="mb-2 flex transform items-center justify-center rounded-lg border border-gray-200 py-2"
        >
          <UIMultiDatePicker
            id="date-override-picker-multi"
            v-model="overriddenDates"
            :custom-style="{ width: '350px' }"
            :panel-view="true"
            :language="locale"
            :hide-input="true"
            :disabled-dates="isDateDisabled"
            :min-date="minDate"
            class="scale-125 transform"
          />
        </div>
        <p
          v-if="showDateSelectionError"
          class="mb-2 mt-2 text-left text-sm text-red-600"
        >
          {{ $t('calendar_advanced.availability.please_select_date') }}
        </p>

        <!-- Time Slots -->
        <div v-if="overriddenDates.length > 0" class="border border-gray-200 p-4 rounded-lg">
          <div class="flex items-center justify-between">
            <p class="mb-3 text-sm font-semibold text-gray-800">
              {{ $t('calendar_advanced.availability.heading_2') }}
            </p>
            <UIButton
              v-if="overriddenHours.length === 0"
              :text="true"
              class="text-gray-700"
              @click="overriddenHours.push({ startTime: '8:0', endTime: '17:0' })"
            >
              <PlusIcon class="w-4" />
            </UIButton>
          </div>

          <div
            ref="customSlotSelectorModal"
            class="max-h-40 overflow-y-auto"
          >
            <div v-if="overriddenHours.length === 0">
              <p class="text-md font-semibold text-gray-600">
                {{ $t('calendar_advanced.availability.unavailable_label') }}
              </p>
            </div>
            <div
              v-for="(item, index) in overriddenHours"
              :key="index"
              class="mb-2 flex items-center gap-2"
            >
              <UITimePicker
                v-model:formatted-value="item.startTime"
                value-format="H:m"
                :minutes="5"
                format="hh:mm a"
                use-12-hours
                :actions="['confirm']"
                class="w-full"
                :status="
                  !item.startTime ||
                  overlappingTimes.includes(index) ||
                  (moment(item.endTime, 'H:m').isSameOrBefore(
                    moment(item.startTime, 'H:m')
                  ) &&
                    item.endTime !== '0:0' &&
                    item.startTime !== '0:0')
                    ? 'error'
                    : 'success'
                "
              />
              <div class="w-8 min-w-max font-semibold capitalize">
                {{ $t('calendar_advanced.availability.to') }}
              </div>
              <UITimePicker
                v-model:formatted-value="item.endTime"
                value-format="H:m"
                :minutes="5"
                format="hh:mm a"
                use-12-hours
                :actions="['confirm']"
                class="w-full"
                :status="
                  !item.endTime ||
                  overlappingTimes.includes(index) ||
                  (moment(item.endTime, 'H:m').isSameOrBefore(
                    moment(item.startTime, 'H:m')
                  ) &&
                    item.endTime !== '0:0' &&
                    item.startTime !== '0:0')
                    ? 'error'
                    : 'success'
                "
              />
              <div class="mr-6 flex items-center justify-around">
                <div class="ml-4 w-4">
                  <UIButton
                    v-if="index === overriddenHours.length - 1"
                    :text="true"
                    class="text-gray-700"
                    :disabled="is24HourSelected"
                    @click="addNextSlots(item)"
                  >
                    <PlusIcon class="w-4" />
                  </UIButton>
                </div>
                <div class="ml-4 w-4">
                  <UIButton
                    class="text-gray-700"
                    :text="true"
                    @click="removeHours(index)"
                  >
                    <Trash01Icon class="w-4" />
                  </UIButton>
                </div>
              </div>
            </div>
          </div>
          <p
            v-if="overlappingTimes.length > 0"
            class="text-left text-sm text-red-600"
          >
            {{ $t('calendar_advanced.availability.overlap_error') }}
          </p>
        </div>
      </UIModalContent>

      <template #footer>
        <UIModalFooter
          id="modal-footer-multi"
          :positive-text="$t('settings.availability.schedule.save_date_specific_hours')"
          :negative-text="$t('settings.calendar.cancel')"
          :disabled="overlappingTimes.length > 0 || invalidTimes || props.isSaving"
          :loading="props.isSaving"
          @positive-click="onClickAddSpecificHours"
          @negative-click="cancelCallback"
        />
      </template>
    </UIModal>

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmation
      v-model:show="showDeleteConfirmModal"
      :header-title="$t('settings.availability.schedule.confirm_delete')"
      :disabled-confirm="props.isSaving"
      @on-confirm="confirmDelete"
      @on-modal-close="cancelDelete"
    >
      <template #message>
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-v-html -->
        <p 
          v-if="deleteTargetUserId"
          class="text-sm leading-5 text-gray-500 [&>b]:font-semibold [&>b]:text-gray-700"
          v-html="$t('settings.availability.schedule.delete_user_date_confirm', { 
            userName: deleteTargetUserName, 
            date: moment(deleteTargetDate).format('MMMM D, YYYY') 
          })"
        />
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-v-html -->
        <p 
          v-else
          class="text-sm leading-5 text-gray-500 [&>b]:font-semibold [&>b]:text-gray-700"
          v-html="$t('settings.availability.schedule.delete_date_confirm', { 
            date: moment(deleteTargetDate).format('MMMM D, YYYY') 
          })"
        />
      </template>
    </DeleteConfirmation>
  </UIContentWrap>
</template>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

:deep(#modal-footer-multi-btn-positive-action) {
  max-width: 200px !important;
  white-space: normal;
  height: auto !important;
  min-height: 34px !important;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  line-height: 1.3 !important;
}
</style>

<style lang="scss">
#do-picker-multi {
  --primary-600: #344054 !important;

  .n-date-panel-weekdays {
    font-weight: 500 !important;
  }

  .n-date-panel-month__prev {
    margin-left: -50px;
    transform: scale(1.5);
  }

  .n-date-panel-month__next {
    margin-right: -50px;
    transform: scale(1.5);
  }

  .n-date-panel-month__fast-next {
    height: 0px !important;
    width: 0px !important;
  }

  .n-date-panel-month__fast-prev {
    height: 0px !important;
    width: 0px !important;
  }

  .n-tabs {
    height: 90%;
    overflow: hidden;
  }

  .n-tab-pane {
    height: 90% !important;
    overflow: scroll !important;
  }
}

</style>

